import React from 'react';
import {Link} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {formatDate} from '../../agency/v2/AlertsPopover';
import {deliveriesState} from './webhookModel';
import {myAlertsPath} from '../alertsCenterPaths';

export const WEBHOOK_LOADING = 'loading';
export const WEBHOOK_OK = 'ok';
export const WEBHOOK_NO_MODULE = 'no_module';
export const WEBHOOK_ERROR = 'error';

function webhookLine(webhook, t, language) {
    if (!webhook || webhook.status === WEBHOOK_LOADING) return {key: 'loading', text: t('alerts_center.rules.channels_webhook_loading')};
    if (webhook.status === WEBHOOK_NO_MODULE) return {key: 'no_module', text: t('alerts_center.rules.channels_webhook_no_module')};
    if (webhook.status !== WEBHOOK_OK) return {key: 'error', text: t('alerts_center.rules.channels_webhook_unknown')};
    if (!webhook.url) return {key: 'none', text: t('alerts_center.rules.channels_webhook_none')};
    const delivery = deliveriesState(webhook.deliveries);
    if (delivery && delivery.state === 'failing') {
        const since = formatDate(delivery.since, language);
        return {
            key: 'failing',
            tone: 'warn',
            text: since
                ? t('alerts_center.rules.channels_webhook_failing', {code: delivery.code || '—', since})
                : t('alerts_center.rules.channels_webhook_failing_never', {code: delivery.code || '—'}),
        };
    }
    return {key: 'ok', text: t('alerts_center.rules.channels_webhook_ok', {count: (webhook.selected || []).length})};
}

/**
 * «Adónde llegan los avisos»: los tres caminos de un aviso con su estado REAL, arriba de la lista.
 *
 * - Correo: cuántas de las alertas de la lista le llegan a quien mira, de cuántas podrían
 *   (`emailCount`, lo cuenta `RulesList` con lo mismo que el filtro «Mandan correo», C-07), con el link
 *   a «Mis alertas», que es donde se eligen las de Dashbo. Sin ese número (no se pudo leer «Mis
 *   alertas»), el texto queda genérico: un «recibes 0» inventado sería peor que no decirlo.
 * - Webhook: configurado y con cuántos eventos, sin configurar, o fallando desde cuándo (con
 *   `deliveries`; sin él, no se afirma nada de los envíos). Lleva a la tarjeta del pie.
 * - Bitácora: siempre.
 */
export function ChannelsCard({emailCount = null, userId, webhook, onGoToWebhook}) {
    const {t, i18n} = useTranslation();
    const hook = webhookLine(webhook, t, i18n.language);
    const myAlerts = <Link key="my-alerts" to={myAlertsPath(userId)} className="alc-link"/>;
    return (
        <section className="alc-card alc-channels" aria-labelledby="alc-channels-title" data-testid="alc-channels">
            <h2 id="alc-channels-title" className="alc-card__title">{t('alerts_center.rules.channels_title')}</h2>
            <ul className="alc-channels__list">
                <li className="alc-channels__item" data-channel="email">
                    {emailCount
                        ? <Trans i18nKey="alerts_center.rules.channels_email" count={emailCount.total}
                                 values={{enabled: emailCount.enabled, total: emailCount.total}}
                                 components={[myAlerts]}/>
                        : <Trans i18nKey="alerts_center.rules.channels_email_unknown" components={[myAlerts]}/>}
                </li>
                <li className={`alc-channels__item${hook.tone ? ` -${hook.tone}` : ''}`} data-channel="webhook"
                    data-state={hook.key}>
                    <span>{hook.text}</span>
                    {hook.key !== 'loading' && (
                        <button type="button" className="alc-link -button" onClick={onGoToWebhook}>
                            {t('alerts_center.rules.channels_webhook_go')}
                        </button>
                    )}
                </li>
                <li className="alc-channels__item" data-channel="bitacora">
                    {t('alerts_center.rules.channels_bitacora')}
                </li>
            </ul>
        </section>
    );
}
